/**
 * LaGift — drag to scroll
 * ---------------------------------------------------------------------------
 * A horizontal scroll container is swipeable on a phone and scrollable with a
 * trackpad, and does nothing at all when someone presses the mouse button and
 * pulls. Most people with a mouse expect a row of cards to drag. This adds
 * exactly that, and only for a mouse: touch and pen already have native
 * behaviour, with momentum and rubber-banding this could only make worse.
 *
 * Used by every horizontal track in the theme, so the behaviour is written once:
 * the product slider, the slideshow, shop the look, and the photo row.
 *
 * Three details that are easy to get wrong:
 *
 *   - `scroll-snap-type: mandatory` fights a drag. The snap engine keeps pulling
 *     the scroll back to the nearest point while the pointer is still moving, so
 *     the row judders and stops. Snapping is switched off for the duration and
 *     restored on release, which lands it on a snap point anyway.
 *   - A card is a link. Without care, dragging across one navigates on release.
 *     The click is swallowed - but only when the pointer actually travelled,
 *     so a plain click still works.
 *   - The cursor should say the row can be dragged, and only when it can. A
 *     grab cursor on a row that fits is a lie, so it is applied from measurement
 *     rather than from markup.
 */
/**
 * Scoped so the file can run more than once.
 *
 * A `<script src>` executes once per tag, not once per URL. Several sections
 * include this, and a merchant can add the same section twice, so on a busy
 * page it runs repeatedly - and a top-level class or const throws
 * "Identifier ... has already been declared" on every run after the first.
 * Inside a function there is nothing shared to collide with.
 */
(() => {
  const DRAG_THRESHOLD = 5;

  class LagiftDragScroll extends HTMLElement {
    connectedCallback() {
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClick = this.onClick.bind(this);
      this.measure = this.measure.bind(this);

      this.addEventListener('pointerdown', this.onPointerDown);
      // Capture, so the click is stopped before it reaches the card's own link.
      this.addEventListener('click', this.onClick, true);

      /**
       * The one that actually breaks this. Links and images are natively
       * draggable, so pressing on a card and pulling starts an HTML5 drag: the
       * browser fires `dragstart`, takes over the pointer, and stops sending
       * `pointermove`. The row moves a few pixels and then stops dead, which
       * looks exactly like a slider that does not drag.
       *
       * `-webkit-user-drag: none` covers the images, but not the anchor wrapped
       * around them. Refusing the drag outright is the reliable answer, and there
       * is nothing inside a product row that anyone wants to drag out of it.
       */
      this.addEventListener('dragstart', (event) => event.preventDefault());

      // Optional controls. A section that wants arrows provides them; one that
      // does not gets a drag-only track and no dead buttons.
      this.prev = this.parentElement?.querySelector('[data-drag-prev]');
      this.next = this.parentElement?.querySelector('[data-drag-next]');
      this.prev?.addEventListener('click', () => this.page(-1));
      this.next?.addEventListener('click', () => this.page(1));
      this.addEventListener('scroll', () => this.updateControls(), { passive: true });

      this.observer = new ResizeObserver(this.measure);
      this.observer.observe(this);
      this.measure();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('click', this.onClick, true);
      this.release();
    }

    /** Whether there is anything to scroll, which decides the cursor. */
    measure() {
      this.scrollable = this.scrollWidth - this.clientWidth > 1;
      this.classList.toggle('is-draggable', this.scrollable);
      this.updateControls();
    }

    updateControls() {
      if (!this.prev && !this.next) return;

      const max = this.scrollWidth - this.clientWidth;
      // A pixel of tolerance: fractional layouts mean scrollLeft rarely reaches
      // the maximum exactly, and a next button that never enables is worse than
      // one that does nothing on the last press.
      if (this.prev) this.prev.disabled = this.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.scrollLeft >= max - 1;

      const hide = !this.scrollable;
      if (this.prev) this.prev.hidden = hide;
      if (this.next) this.next.hidden = hide;
    }

    /**
     * One viewport of items, which is what an arrow press should move.
     *
     * Snapping has to come off for the ride. Against `scroll-snap-type: mandatory`
     * a smooth programmatic scroll has its animation cancelled and the snap engine
     * puts the scroll straight back - measured here as scrollLeft never leaving 0,
     * so the arrow looked dead while an instant scroll worked fine. Snapping is
     * restored once the scroll settles, which lands it on a snap point anyway.
     */
    page(direction) {
      const left = this.scrollLeft + direction * this.clientWidth;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.scrollTo({ left, behavior: 'auto' });
        return;
      }

      /**
       * The snap style has to actually land before the scroll is asked for.
       *
       * Setting `scrollSnapType` and calling `scrollTo` in the same task does not
       * work: the style change has not been applied yet, the snap engine still
       * sees `mandatory`, and it cancels the smooth animation exactly as if
       * snapping had never been turned off. Measured here as scrollLeft never
       * leaving 0 while an instant scroll moved fine.
       *
       * One frame is enough.
       */
      this.style.scrollSnapType = 'none';
      requestAnimationFrame(() => {
        this.scrollTo({ left, behavior: 'smooth' });

        /**
         * A smooth scroll against a snap container is refused often enough, and
         * for subtle enough reasons, that it is not worth trusting. If nothing
         * has moved shortly after asking, go there directly.
         *
         * An arrow that does nothing is the worst outcome available here; a jump
         * instead of a glide is a very cheap price for never shipping one.
         */
        const from = this.scrollLeft;
        setTimeout(() => {
          if (this.scrollLeft === from && Math.round(from) !== Math.round(left)) {
            this.scrollTo({ left, behavior: 'auto' });
          }
          this.restoreSnapWhenSettled();
        }, 120);
      });
    }

    /**
     * Snapping goes back on once the scroll has actually stopped, and not before.
     *
     * The obvious signals are both wrong here. A fixed timer restores snapping
     * mid-animation on a short scroll, and the snap engine then cancels the
     * animation and pulls the row back where it started - the arrow appears to do
     * nothing, which is exactly the bug this was meant to fix. `scrollend` is not
     * reliable either: it can arrive before the animation has begun.
     *
     * So watch the position instead. Two frames at the same offset means it has
     * settled, whatever caused it to stop, with a ceiling so a scroll that never
     * settles cannot leave snapping off for good.
     */
    restoreSnapWhenSettled() {
      cancelAnimationFrame(this.snapFrame);
      const started = performance.now();
      let last = null;
      let still = 0;

      const check = () => {
        if (this.dragging) return; // the drag has its own release
        const at = this.scrollLeft;
        still = at === last ? still + 1 : 0;
        last = at;

        if (still >= 2 || performance.now() - started > 1200) {
          this.style.scrollSnapType = '';
          return;
        }
        this.snapFrame = requestAnimationFrame(check);
      };

      this.snapFrame = requestAnimationFrame(check);
    }

    onPointerDown(event) {
      // Touch and pen keep the browser's own handling.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!this.scrollable) return;
      // Anything the mouse is genuinely needed for is left alone.
      if (event.target.closest('input, textarea, select, [contenteditable]')) return;

      /**
       * Nothing happens yet.
       *
       * This used to capture the pointer here, and that quietly broke every
       * click inside the row: a captured pointer retargets its own events AND
       * the compatibility mouse events to the capturing element, so `pointerup`
       * and the `click` derived from it were delivered to the row instead of to
       * the card's link or the add to cart button. Clicking a product did
       * nothing at all, on any row long enough to scroll.
       *
       * A press is not a drag. The capture is taken in onPointerMove, once the
       * pointer has actually travelled - so a plain click never involves capture
       * and behaves exactly as it would with no script here at all.
       */
      this.pressed = true;
      this.moved = false;
      this.startX = event.clientX;
      this.startScroll = this.scrollLeft;

      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerUp);
    }

    /** Promote a press into a drag: this is where the row takes over. */
    beginDrag(event) {
      this.dragging = true;
      this.moved = true;

      // Held off until now so a click is never affected by it.
      this.style.scrollSnapType = 'none';
      this.classList.add('is-dragging');

      try {
        this.setPointerCapture(event.pointerId);
        this.pointerId = event.pointerId;
      } catch {
        // The pointer can already be gone on a very fast gesture. The drag just
        // ends early; nothing else depends on the capture.
      }
    }

    onPointerMove(event) {
      if (!this.pressed) return;

      const dx = event.clientX - this.startX;
      if (!this.dragging && Math.abs(dx) < DRAG_THRESHOLD) return;

      if (!this.dragging) this.beginDrag(event);

      // Stops the browser starting a text selection or an image drag now that
      // this is a real drag rather than a click that has not finished.
      event.preventDefault();
      this.scrollLeft = this.startScroll - dx;
    }

    onPointerUp() {
      this.release();
    }

    release() {
      if (!this.pressed) return;
      this.pressed = false;
      const wasDragging = this.dragging;
      this.dragging = false;

      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('pointercancel', this.onPointerUp);

      if (this.pointerId !== undefined && this.hasPointerCapture?.(this.pointerId)) {
        this.releasePointerCapture(this.pointerId);
      }
      this.pointerId = undefined;

      if (wasDragging) {
        this.classList.remove('is-dragging');
        // Removing the inline value hands the row back to the stylesheet, which
        // snaps it to the nearest item.
        this.style.scrollSnapType = '';
      }

      /**
       * A drag ends and the browser fires one click on release, which has to be
       * swallowed - otherwise dragging across a product card opens it.
       *
       * That used to be a flag the click itself cleared, which is a trap: if the
       * click never arrives - the drag ended outside the element, the pointer
       * was cancelled, the release landed on something that stopped it - the
       * flag stays set and eats the NEXT click instead. The card then simply
       * does not open, once, for no reason the customer can see.
       *
       * A deadline cannot get stuck. There is nothing to reset, and a click a
       * moment later is always a real one.
       */
      if (this.moved) this.ignoreClicksUntil = performance.now() + 300;
      this.moved = false;
    }

    onClick(event) {
      if (!this.ignoreClicksUntil || performance.now() > this.ignoreClicksUntil) return;

      this.ignoreClicksUntil = 0;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  if (!customElements.get('lagift-drag-scroll')) {
    customElements.define('lagift-drag-scroll', LagiftDragScroll);
  }
})();
